/**
 * The bell. Unread count on the badge, the latest 30 in the panel.
 *
 * POLLS EVERY 60 SECONDS, and the poll is also what syncs: the server pulls
 * finished processing jobs into the store when somebody looks, rather than
 * the pipeline writing rows as it goes. The events worth a notification are
 * mostly FAILURES in detached jobs, which is exactly when a process is least
 * able to write a tidy row about itself.
 *
 * OPENING THE PANEL DOES NOT MARK ANYTHING READ. Read is a deliberate act —
 * "I saw this" should mean somebody saw it, not that a badge was in the way.
 */
import { Bell } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { api } from '../../lib/api'
import './notifications.css'

type Row = {
  id: number
  kind: string
  title: string
  body: string | null
  link: string | null
  created_at: string | null
  read: boolean
}
type Payload = { notifications: Row[]; unread: number }

const POLL_MS = 60_000

// The API sends UTC instants (tz-aware ISO strings); render them in the
// viewer's timezone. Slicing the raw string showed UTC as if it were local.
const WHEN_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit',
}
const when = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, WHEN_FORMAT)
}

export default function NotificationsBell() {
  const [data, setData] = useState<Payload>({ notifications: [], unread: 0 })
  const [open, setOpen] = useState(false)
  const panel = useRef<HTMLDivElement | null>(null)
  const portalRef = useRef<HTMLDivElement | null>(null)
  // Where to draw the panel, in viewport coordinates.
  //
  // It is PORTALLED to <body> rather than rendered in place, because the
  // sidebar it lives in sets `overflow: hidden` — needed to clip the labels
  // when the rail collapses — and that clipped the panel to the rail's width
  // no matter how high its z-index went. A z-index cannot escape an
  // overflow, so the panel has to leave the subtree.
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const btn = panel.current?.querySelector('.notif__btn')
      if (!btn) return
      const r = btn.getBoundingClientRect()
      setAnchor({ left: r.left, bottom: window.innerHeight - r.top + 8 })
    }
    place()
    // Collapsing the rail moves the bell; so does resizing or scrolling a
    // page behind an open panel.
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  const load = useCallback(() => {
    api<Payload>('/api/v1/notifications')
      .then(setData)
      // A failed poll is not worth a banner in the chrome; the next one
      // retries, and the bell showing a stale count beats the rail breaking.
      .catch(() => { /* keep what we have */ })
  }, [])

  useEffect(() => {
    load()
    const t = window.setInterval(load, POLL_MS)
    return () => window.clearInterval(t)
  }, [load])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panel.current && !panel.current.contains(t)
          && !(portalRef.current && portalRef.current.contains(t)))
        setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const markAllRead = async () => {
    await api('/api/v1/notifications/read', { method: 'POST' })
    load()
  }

  const { unread, notifications } = data

  return (
    <div className="notif" ref={panel}>
      <button
        className="notif__btn"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={16} aria-hidden />
        {unread > 0 && (
          <span className="notif__badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && anchor && createPortal(
        <div
          ref={portalRef}
          className="notif__panel"
          role="dialog"
          aria-label="Notifications"
          style={{ left: anchor.left, bottom: anchor.bottom }}
        >
          <div className="notif__head">
            <span>Notifications</span>
            <button className="notif__read" onClick={() => void markAllRead()}
                    disabled={unread === 0}>Mark all read</button>
          </div>
          <div className="notif__list">
            {notifications.length === 0 && (
              <p className="notif__empty">
                Nothing yet. Processing results land here.
              </p>
            )}
            {notifications.map((n) => {
              const inner = (
                <>
                  <span className="notif__title">{n.title}</span>
                  {n.body && <span className="notif__body">{n.body}</span>}
                  <span className="notif__when">{when(n.created_at)}</span>
                </>
              )
              const cls = `notif__row${n.read ? '' : ' is-unread'}`
              return n.link
                ? <a className={cls} key={n.id} href={n.link}>{inner}</a>
                : <div className={cls} key={n.id}>{inner}</div>
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
